import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import IconArrow from '../../components/icons/IconArrow';
import IconDelete from '../../components/icons/IconDelete';
import IconAdd from '../../components/icons/IconAdd';
import IconEdit from '../../components/icons/IconEdit';
import ContainerLayout from '../../components/ContainerLayout';
import ContainerConfigForm from '../../components/ContainerConfigForm';
import TopicElementCard from './TopicElementCard';
import { CONTAINER_LAYOUT_TYPE_LIST, getContainerLayoutType, safeParseContainerConfig } from '../../variables/containerLayoutTypes';
import { TOPIC_BLOCK_TYPE_LIST } from './topicBlockTypes';
import { mergeTopicContent, nextTopicContentPosition, computeTopicContentMove } from './topicContent';
import { buildTopicElementPayload, buildTopicContainerPayload } from './coursesPayload';
import { makeDndId, makeEmptyDropzoneId } from '../../components/dnd/dndId';
import { applyDragPreview } from '../../components/dnd/containerDragPreview';
import * as api from './api';

const TOPIC_ELEMENT_BASE = '/api/topicelements';
const TOPIC_CONTAINER_BASE = '/api/topiccontainers';

// A layout region holding its own scoped subset of the topic's
// TopicElement blocks (never exercise-links, never another container -
// see topicContent.js's header comment). Move/delete for the container
// ITSELF (its position among the topic's other top-level items) comes
// from the parent exactly like TopicElementCard's own onMove/onDeleted -
// but everything about this container's CHILDREN (add/move/delete a
// block inside it) is handled locally here, since no other component
// needs to orchestrate that, mirroring how every other card in this
// module owns its own mutation calls.
export default function TopicContainerCard({ topicId, courseId, container, allElements, canMoveUp, canMoveDown, onMove, onDeleted, onReload, dragPreview }) {
   const [configOpen, setConfigOpen] = useState(false);
   const [pickerOpen, setPickerOpen] = useState(false);
   const layoutType = getContainerLayoutType(container.layout) || CONTAINER_LAYOUT_TYPE_LIST[0];
   const config = safeParseContainerConfig(container.config, container.layout);

   // Containers never nest and never themselves get dragged (`disabled:
   // true`) - this just lets the card occupy its slot in the parent's
   // top-level SortableContext so neighbor-reflow math around it stays
   // correct. `rawChildren` (canonical) drives move-button math; `children`
   // (drag-preview-adjusted) drives what's actually rendered - see
   // TopicElementList.jsx's header comment for why canonical data is never
   // mutated during a drag. Deliberately not applying this hook's own
   // transform/transition (see TopicElementCard.jsx's matching comment for
   // why - it fed a real collision-detection feedback loop during
   // development), only its droppable-slot registration is needed here.
   const { setNodeRef: setContainerSlotRef } = useSortable({ id: makeDndId('container', container.id), disabled: true });

   const { setNodeRef: setEmptyDropRef, isOver: isEmptyOver } = useDroppable({ id: makeEmptyDropzoneId(container.id) });

   const rawChildren = mergeTopicContent({ elements: allElements, containers: [], containerId: container.id })
      .map((entry) => ({ ...entry, dndId: makeDndId('block', entry.item.id) }));
   const children = applyDragPreview(rawChildren, dragPreview, container.id);

   const handleDelete = () => {
      if (children.length > 0) {
         window.alert('Неможливо видалити контейнер: спочатку видаліть усі елементи всередині нього.');
         return;
      }
      if (!window.confirm('Видалити цей контейнер?')) return;
      api.deleteOne(TOPIC_CONTAINER_BASE, container.id).then(() => onDeleted()).catch(() => {
         window.alert('Не вдалося видалити контейнер.');
      });
   };

   const saveContainer = (changes) => {
      const payload = buildTopicContainerPayload(topicId, container, changes);
      return api.updateOne(TOPIC_CONTAINER_BASE, container.id, payload).then(() => onReload());
   };

   const handleLayoutChange = (newLayout) => {
      const newType = getContainerLayoutType(newLayout);
      saveContainer({ layout: newLayout, config: JSON.stringify(newType.defaultConfig) });
   };

   const handleConfigChange = (nextConfig) => {
      saveContainer({ config: JSON.stringify(nextConfig) });
   };

   const handleAddBlock = (blockType) => {
      setPickerOpen(false);
      const position = nextTopicContentPosition(rawChildren);
      const draft = { type: blockType.type, config: JSON.stringify(blockType.defaultConfig), position, container: container.id };
      api.createOne(TOPIC_ELEMENT_BASE, buildTopicElementPayload(topicId, draft))
         .then(() => onReload())
         .catch((err) => console.error(err));
   };

   // Bound to rawChildren (canonical), not the drag-preview-adjusted
   // `children` - move-button clicks and an active pointer-drag can't
   // realistically happen at the same time (one pointer, and dragging
   // captures it), so this stays correct in practice; using the canonical
   // array here avoids ever computing a swap against a transient preview
   // ordering that hasn't actually been committed to the server.
   const handleMoveChild = (index, direction) => {
      const swap = computeTopicContentMove(rawChildren, index, direction);
      if (!swap) return;
      Promise.all(swap.map((entry) => api.updateOne(TOPIC_ELEMENT_BASE, entry.item.id, buildTopicElementPayload(topicId, entry.item))))
         .then(() => onReload())
         .catch((err) => console.error(err));
   };

   return (
      <div ref={setContainerSlotRef} style={{ '--accent': '#7ec2e8' }} className='exercise-block-card topic-container-card'>
         <div className='exercise-block-card-toolbar'>
            <span className='exercise-block-card-type-label'>Контейнер: {layoutType.label}</span>
            <div className='exercise-block-card-actions'>
               <div className={'exercise-block-move-btn' + (!canMoveUp ? ' disabled' : '')} onClick={() => canMoveUp && onMove(-1)} title='Вгору'>
                  <IconArrow style={{ transform: 'rotate(-90deg)', height: 14, width: 14 }} />
               </div>
               <div className={'exercise-block-move-btn' + (!canMoveDown ? ' disabled' : '')} onClick={() => canMoveDown && onMove(1)} title='Вниз'>
                  <IconArrow style={{ transform: 'rotate(90deg)', height: 14, width: 14 }} />
               </div>
               <div className='exercise-block-edit-btn' onClick={() => setConfigOpen((prev) => !prev)} title='Налаштування'>
                  <IconEdit style={{ fill: '#7ec2e8', width: 14, height: 14, margin: 0 }} />
               </div>
               <div className='exercise-block-delete-btn' onClick={handleDelete} title='Видалити'>
                  <IconDelete style={{ fill: '#e0876c', width: 14, height: 14, margin: 0 }} />
               </div>
            </div>
         </div>

         {configOpen ? (
            <ContainerConfigForm layoutKey={layoutType.key} config={config}
               onLayoutChange={handleLayoutChange} onConfigChange={handleConfigChange} />
         ) : ''}

         <div className='exercise-block-card-body'>
            {rawChildren.length > 0 ? (
               <SortableContext items={children.map((entry) => entry.dndId)} strategy={rectSortingStrategy}>
                  <ContainerLayout layout={container.layout} config={config}>
                     {children.map((entry, index) => (
                        entry.isDropPlaceholder ? (
                           <div key='drop-placeholder' className='exercise-block-drop-placeholder' />
                        ) : (
                           <TopicElementCard key={entry.item.id} topicId={topicId} courseId={courseId} block={entry.item}
                              canMoveUp={index > 0} canMoveDown={index < children.length - 1}
                              onMove={(direction) => handleMoveChild(index, direction)}
                              onDeleted={onReload} onSaved={onReload} />
                        )
                     ))}
                  </ContainerLayout>
               </SortableContext>
            ) : (
               // Canonically-empty container: the droppable id here stays
               // registered the whole time regardless of whether a preview
               // is currently active, rather than switching render branches
               // on `children.length` (which would briefly de-register and
               // re-register this droppable exactly while the pointer is
               // hovering it, causing the same kind of flip-flop the
               // placeholder above was built to avoid).
               <div ref={setEmptyDropRef} className={'exercise-block-dropzone' + (isEmptyOver ? ' exercise-block-dropzone-over' : '')}>
                  {children.length > 0 ? (
                     <div className='exercise-block-drop-placeholder' />
                  ) : (
                     <p className='exercise-block-empty-hint'>Контейнер порожній - додайте блок нижче.</p>
                  )}
               </div>
            )}

            <div className='exercise-block-add-wrap'>
               <div className='exercise-block-add-trigger' onClick={() => setPickerOpen((prev) => !prev)}>
                  <IconAdd />
                  <span>Додати блок у контейнер</span>
               </div>
               {pickerOpen ? (
                  <div className='exercise-block-type-picker'>
                     <div className='exercise-block-type-row'>
                        {TOPIC_BLOCK_TYPE_LIST.map((bt) => (
                           <div key={bt.type} className='exercise-block-type-chip' style={{ '--accent': bt.accent }} onClick={() => handleAddBlock(bt)}>
                              {bt.label}
                           </div>
                        ))}
                     </div>
                  </div>
               ) : ''}
            </div>
         </div>
      </div>
   );
}
